import type { Trade } from "./mock-data";

// ── Types ──

export type TradeAccounting = {
  tradeId: number;
  direction: Trade["direction"];
  // Sell trades: P&L (only populated when cost basis is available)
  revenue: number | null; // sell_rate × qty (total trade revenue)
  costOfSales: number | null; // WAC × costedQty
  grossProfit: number | null; // (sell_rate - WAC) × costedQty
  marginPct: number | null; // grossProfit / (sell_rate × costedQty) × 100
  wacAtSale: number | null; // WAC at time of this sell
  costedQty: number | null; // how much of the sell qty had cost basis
  // Buy trades: inventory addition
  inventoryAdded: number | null;
  inventoryCost: number | null;
  // Borrow/return/lend
  borrowedDelta: number | null;
  lentDelta: number | null;
  lentCostBasis: number | null;
};

export type InventoryState = {
  totalQuantity: number; // costedQuantity + uncostedQuantity
  costedQuantity: number; // USDT where we know the NGN cost
  totalCost: number; // total NGN cost of costed inventory
  wac: number; // weighted average cost (NGN per USDT) = totalCost / costedQuantity
  uncostedQuantity: number; // USDT with no rate (exchange buys)
};

export type PositionState = {
  ownedUsdt: number; // inventory (your USDT)
  borrowedUsdt: number; // liability
  lentUsdt: number; // receivable
};

export type FiatPosition = {
  ngnOut: number; // total paid out (buying)
  ngnIn: number; // total received (selling)
  netFlow: number; // ngnIn - ngnOut
  pendingIn: number; // expected but unsettled (sell trades)
  pendingOut: number; // owed but unsettled (buy trades)
};

export type PeriodPnL = {
  grossRevenue: number; // sum of (sell_rate × costedQty) — only costed portions
  costOfSales: number; // sum of (WAC × costedQty)
  grossProfit: number; // grossRevenue - costOfSales
  marginPct: number | null;
  totalBought: number;
  totalSold: number;
  buyCount: number;
  sellCount: number;
  avgBuyRate: number | null;
  avgSellRate: number | null;
  middlemanProfit: number; // sum of middleman deal profits
};

export type AccountingWarning = {
  tradeId: number;
  type: "no_rate" | "no_cost_basis" | "partial_cost_basis" | "negative_inventory";
  message: string;
};

export type LedgerAccounting = {
  trades: Map<number, TradeAccounting>;
  inventory: InventoryState;
  position: PositionState;
  fiat: FiatPosition;
  allTime: PeriodPnL;
  today: PeriodPnL;
  warnings: AccountingWarning[];
};

// ── Helpers ──

export function getWATDateKey(d: Date): string {
  // Always use Africa/Lagos (WAT) for day boundaries regardless of runtime TZ
  return d.toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" }); // YYYY-MM-DD
}

export function isToday(dateStr: string): boolean {
  return getWATDateKey(new Date(dateStr)) === getWATDateKey(new Date());
}

export function isYesterday(dateStr: string): boolean {
  const tradeKey = getWATDateKey(new Date(dateStr));
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return tradeKey === getWATDateKey(yesterday);
}

export function isWithinDays(dateStr: string, n: number): boolean {
  const todayKey = getWATDateKey(new Date());
  const tradeKey = getWATDateKey(new Date(dateStr));
  // Compare using calendar days in WAT
  const todayMs = new Date(todayKey + "T00:00:00").getTime();
  const tradeMs = new Date(tradeKey + "T00:00:00").getTime();
  const diffDays = (todayMs - tradeMs) / 86_400_000;
  return diffDays >= 0 && diffDays < n;
}

function emptyPeriod(): PeriodPnL {
  return {
    grossRevenue: 0,
    costOfSales: 0,
    grossProfit: 0,
    marginPct: null,
    totalBought: 0,
    totalSold: 0,
    buyCount: 0,
    sellCount: 0,
    avgBuyRate: null,
    avgSellRate: null,
    middlemanProfit: 0,
  };
}

/** Recalculate WAC from costed inventory. Never rounds. */
function recalcWac(costedQty: number, totalCost: number): number {
  return costedQty > 0 ? totalCost / costedQty : 0;
}

// ── Main Computation ──

export function computeLedgerAccounting(trades: Trade[]): LedgerAccounting {
  // Sort oldest first for correct WAC progression
  const sorted = [...trades]
    .filter((t) => t.status !== "expired")
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  // Inventory state — no rounding, full precision
  let costedQty = 0;
  let uncostedQty = 0;
  let totalCost = 0; // NGN cost of costed inventory
  let wac = 0; // NGN per USDT

  // Position
  let borrowedUsdt = 0;
  let lentUsdt = 0;

  // Fiat
  let ngnOut = 0;
  let ngnIn = 0;
  let pendingIn = 0;
  let pendingOut = 0;

  const tradeMap = new Map<number, TradeAccounting>();
  const warnings: AccountingWarning[] = [];

  // Period accumulators
  const allTime = emptyPeriod();
  const today = emptyPeriod();

  // Rate accumulators for weighted averages
  let buyRateNgn = 0; // sum of (rate × qty) for buys with rate
  let buyRateQty = 0;
  let sellRateNgn = 0;
  let sellRateQty = 0;
  let todayBuyRateNgn = 0;
  let todayBuyRateQty = 0;
  let todaySellRateNgn = 0;
  let todaySellRateQty = 0;

  for (const trade of sorted) {
    const qty =
      trade.coinAmountSettled > 0 ? trade.coinAmountSettled : trade.usdtAmount;
    const rate = trade.rate;
    const isTd = isToday(trade.createdAt);
    const isSettled = trade.status === "settled";

    // Use settled naira for fiat tracking, rate × qty for P&L
    const ngnSettled =
      trade.nairaAmountSettled > 0
        ? trade.nairaAmountSettled
        : trade.ngnAmount || 0;

    const acct: TradeAccounting = {
      tradeId: trade.id,
      direction: trade.direction,
      revenue: null,
      costOfSales: null,
      grossProfit: null,
      marginPct: null,
      wacAtSale: null,
      costedQty: null,
      inventoryAdded: null,
      inventoryCost: null,
      borrowedDelta: null,
      lentDelta: null,
      lentCostBasis: null,
    };

    switch (trade.direction) {
      case "buy": {
        if (rate != null && rate > 0) {
          // Costed buy — adds to inventory with known cost
          const cost = qty * rate;
          costedQty += qty;
          totalCost += cost;
          wac = recalcWac(costedQty, totalCost);

          acct.inventoryAdded = qty;
          acct.inventoryCost = cost;

          // Fiat tracking (actual cash movements)
          if (isSettled) {
            ngnOut += ngnSettled;
          } else {
            pendingOut += trade.ngnAmount || 0;
          }

          // Period stats
          allTime.totalBought += qty;
          allTime.buyCount++;
          buyRateNgn += rate * qty;
          buyRateQty += qty;
          if (isTd) {
            today.totalBought += qty;
            today.buyCount++;
            todayBuyRateNgn += rate * qty;
            todayBuyRateQty += qty;
          }
        } else {
          // Exchange buy with no rate — quantity known, cost unknown
          uncostedQty += qty;
          acct.inventoryAdded = qty;
          acct.inventoryCost = null;

          allTime.totalBought += qty;
          allTime.buyCount++;
          if (isTd) {
            today.totalBought += qty;
            today.buyCount++;
          }

          warnings.push({
            tradeId: trade.id,
            type: "no_rate",
            message: `Buy ${qty.toLocaleString()} USDT — no rate, excluded from cost basis`,
          });
        }
        break;
      }

      case "sell": {
        if (rate == null || rate <= 0) {
          // Sell with no rate — can't calculate anything meaningful
          // Still reduce inventory
          const reduceCosted = Math.min(qty, costedQty);
          const reduceUncosted = qty - reduceCosted;
          if (reduceCosted > 0) {
            totalCost -= wac * reduceCosted;
            costedQty -= reduceCosted;
          }
          if (reduceUncosted > 0) {
            uncostedQty = Math.max(0, uncostedQty - reduceUncosted);
          }
          wac = recalcWac(costedQty, totalCost);

          warnings.push({
            tradeId: trade.id,
            type: "no_rate",
            message: `Sell ${qty.toLocaleString()} USDT — no rate, P&L not calculated`,
          });

          allTime.totalSold += qty;
          allTime.sellCount++;
          if (isTd) {
            today.totalSold += qty;
            today.sellCount++;
          }
          break;
        }

        // Sell with rate — calculate P&L on costed portion only
        const totalInv = costedQty + uncostedQty;

        // How much of this sell comes from costed vs uncosted inventory
        const sellFromCosted = Math.min(qty, costedQty);
        const sellFromUncosted = Math.min(qty - sellFromCosted, uncostedQty);
        const sellBeyondInv = qty - sellFromCosted - sellFromUncosted;

        // Revenue is rate × qty for the whole trade
        acct.revenue = rate * qty;
        acct.wacAtSale = wac;

        if (sellFromCosted > 0 && wac > 0) {
          // P&L only on the costed portion
          const costBasis = wac * sellFromCosted;
          const costedRevenue = rate * sellFromCosted;
          const profit = costedRevenue - costBasis;

          acct.costOfSales = costBasis;
          acct.grossProfit = profit;
          acct.costedQty = sellFromCosted;
          acct.marginPct =
            costedRevenue > 0 ? (profit / costedRevenue) * 100 : null;

          // Period P&L — only costed portions go into P&L totals
          allTime.grossRevenue += costedRevenue;
          allTime.costOfSales += costBasis;
          if (isTd) {
            today.grossRevenue += costedRevenue;
            today.costOfSales += costBasis;
          }

          // Reduce costed inventory
          totalCost -= costBasis;
          costedQty -= sellFromCosted;
          // WAC stays the same after sell (WAC method property)
          // But guard against float drift
          if (costedQty <= 0) {
            costedQty = 0;
            totalCost = 0;
            wac = 0;
          }
        } else {
          // WAC is 0 or no costed inventory — cannot calculate P&L
          acct.grossProfit = null;
          acct.costOfSales = null;
          acct.costedQty = 0;

          warnings.push({
            tradeId: trade.id,
            type: "no_cost_basis",
            message: `Sell ${qty.toLocaleString()} USDT at ${rate.toLocaleString()} — no cost basis, P&L excluded`,
          });
        }

        // Reduce uncosted inventory
        if (sellFromUncosted > 0) {
          uncostedQty -= sellFromUncosted;
        }

        // Warn if partial cost basis
        if (
          sellFromCosted > 0 &&
          sellFromCosted < qty &&
          (sellFromUncosted > 0 || sellBeyondInv > 0)
        ) {
          warnings.push({
            tradeId: trade.id,
            type: "partial_cost_basis",
            message: `Sell ${qty.toLocaleString()} USDT — only ${sellFromCosted.toLocaleString()} had cost basis`,
          });
        }

        // Warn if selling more than inventory
        if (sellBeyondInv > 0) {
          warnings.push({
            tradeId: trade.id,
            type: "negative_inventory",
            message: `Sold ${sellBeyondInv.toLocaleString()} USDT more than available inventory`,
          });
        }

        // Fiat tracking (actual cash movements from selling)
        if (isSettled) {
          ngnIn += ngnSettled;
        } else {
          pendingIn += trade.ngnAmount || 0;
        }

        // Period volume stats
        allTime.totalSold += qty;
        allTime.sellCount++;
        sellRateNgn += rate * qty;
        sellRateQty += qty;
        if (isTd) {
          today.totalSold += qty;
          today.sellCount++;
          todaySellRateNgn += rate * qty;
          todaySellRateQty += qty;
        }
        break;
      }

      case "borrow": {
        // Liability — you owe this USDT back
        // Also adds to available pool at current WAC for P&L tracking
        borrowedUsdt += qty;
        acct.borrowedDelta = qty;

        if (wac > 0) {
          // Add borrowed USDT to costed inventory at current WAC
          // This means selling borrowed USDT calculates P&L at (sell_rate - WAC)
          // WAC doesn't change because we're adding at WAC
          const borrowCost = wac * qty;
          costedQty += qty;
          totalCost += borrowCost;
          // WAC unchanged: (oldCost + wac*qty) / (oldQty + qty) = wac
        } else {
          // No cost basis available — add as uncosted
          uncostedQty += qty;
        }
        break;
      }

      case "return": {
        // Reduces liability and available pool
        borrowedUsdt = Math.max(0, borrowedUsdt - qty);
        acct.borrowedDelta = -qty;

        // Remove from inventory — prefer costed first
        const returnCosted = Math.min(qty, costedQty);
        const returnUncosted = qty - returnCosted;
        if (returnCosted > 0) {
          totalCost -= wac * returnCosted;
          costedQty -= returnCosted;
          if (costedQty <= 0) {
            costedQty = 0;
            totalCost = 0;
            wac = 0;
          }
        }
        if (returnUncosted > 0) {
          uncostedQty = Math.max(0, uncostedQty - returnUncosted);
        }
        break;
      }

      case "lend": {
        // Removes from inventory, creates receivable — no P&L
        lentUsdt += qty;
        acct.lentDelta = qty;

        const lendCosted = Math.min(qty, costedQty);
        const lendUncosted = qty - lendCosted;
        if (lendCosted > 0) {
          const lendCost = wac * lendCosted;
          totalCost -= lendCost;
          costedQty -= lendCosted;
          acct.lentCostBasis = lendCost;
          if (costedQty <= 0) {
            costedQty = 0;
            totalCost = 0;
            wac = 0;
          }
        }
        if (lendUncosted > 0) {
          uncostedQty = Math.max(0, uncostedQty - lendUncosted);
        }
        break;
      }

      case "personal":
      case "lock":
        // No inventory or P&L impact
        break;

      case "middleman": {
        // No inventory impact — coin never touches your wallet
        // Only profit matters
        const profit = trade.middlemanProfit ?? 0;
        acct.grossProfit = profit;
        acct.revenue = profit;
        acct.costOfSales = 0;

        if (isSettled && profit > 0) {
          ngnIn += profit;
        }

        allTime.middlemanProfit += profit;
        if (isTd) {
          today.middlemanProfit += profit;
        }
        break;
      }
    }

    tradeMap.set(trade.id, acct);
  }

  // Finalize period totals
  allTime.grossProfit = allTime.grossRevenue - allTime.costOfSales;
  allTime.marginPct =
    allTime.grossRevenue > 0
      ? (allTime.grossProfit / allTime.grossRevenue) * 100
      : null;
  allTime.avgBuyRate = buyRateQty > 0 ? buyRateNgn / buyRateQty : null;
  allTime.avgSellRate = sellRateQty > 0 ? sellRateNgn / sellRateQty : null;

  today.grossProfit = today.grossRevenue - today.costOfSales;
  today.marginPct =
    today.grossRevenue > 0
      ? (today.grossProfit / today.grossRevenue) * 100
      : null;
  today.avgBuyRate =
    todayBuyRateQty > 0 ? todayBuyRateNgn / todayBuyRateQty : null;
  today.avgSellRate =
    todaySellRateQty > 0 ? todaySellRateNgn / todaySellRateQty : null;

  const totalQuantity = costedQty + uncostedQty;

  return {
    trades: tradeMap,
    inventory: {
      totalQuantity,
      costedQuantity: costedQty,
      totalCost,
      wac,
      uncostedQuantity: uncostedQty,
    },
    position: {
      ownedUsdt: totalQuantity,
      borrowedUsdt,
      lentUsdt,
    },
    fiat: {
      ngnOut,
      ngnIn,
      netFlow: ngnIn - ngnOut,
      pendingIn,
      pendingOut,
    },
    allTime,
    today,
    warnings,
  };
}

// ── Post-processing: period-specific P&L ──

export function computePeriodPnL(
  trades: Trade[],
  accountingMap: Map<number, TradeAccounting>,
  dateFilter: (dateStr: string) => boolean,
): PeriodPnL {
  const period = emptyPeriod();
  let buyRateNgn = 0;
  let buyRateQty = 0;
  let sellRateNgn = 0;
  let sellRateQty = 0;

  for (const trade of trades) {
    if (trade.status === "expired") continue;
    if (!dateFilter(trade.createdAt)) continue;

    const qty =
      trade.coinAmountSettled > 0 ? trade.coinAmountSettled : trade.usdtAmount;
    const rate = trade.rate;
    const acct = accountingMap.get(trade.id);

    switch (trade.direction) {
      case "buy": {
        period.totalBought += qty;
        period.buyCount++;
        if (rate != null && rate > 0) {
          buyRateNgn += rate * qty;
          buyRateQty += qty;
        }
        break;
      }
      case "sell": {
        period.totalSold += qty;
        period.sellCount++;
        if (rate != null && rate > 0) {
          sellRateNgn += rate * qty;
          sellRateQty += qty;
        }
        // Sum P&L from already-computed accounting
        if (acct && acct.grossProfit != null && acct.costOfSales != null && acct.costedQty != null && acct.costedQty > 0) {
          period.grossRevenue += rate! * acct.costedQty;
          period.costOfSales += acct.costOfSales;
        }
        break;
      }
      case "middleman": {
        const profit = trade.middlemanProfit ?? 0;
        period.middlemanProfit += profit;
        break;
      }
      // borrow/return/lend/personal/lock — no P&L impact
    }
  }

  period.grossProfit = period.grossRevenue - period.costOfSales;
  period.marginPct =
    period.grossRevenue > 0
      ? (period.grossProfit / period.grossRevenue) * 100
      : null;
  period.avgBuyRate = buyRateQty > 0 ? buyRateNgn / buyRateQty : null;
  period.avgSellRate = sellRateQty > 0 ? sellRateNgn / sellRateQty : null;

  return period;
}
